import React, { useState } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Box,
  FormHelperText,
  Chip,
  Typography,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import { useMenuScrollPagination } from "../../hooks/useMenuScrollPagination";

interface Option {
  label: string;
  value: string | number;
}

interface AsyncMultiAutoCompleteProps {
  label: string;
  value: Option[];
  onChange: (value: Option[]) => void;
  onRegister?: (items: Option[]) => void;
  options: Option[];
  onSearch: (text: string) => void;
  onScrollEnd?: () => void;
  loading?: boolean;
  error?: boolean;
  helperText?: string | null;
  required?: boolean;
  disabled?: boolean;
  totalCount?: number;
}

export const AsyncMultiAutoComplete: React.FC<AsyncMultiAutoCompleteProps> = ({
  label,
  value,
  onChange,
  onRegister,
  options,
  onSearch,
  onScrollEnd,
  loading = false,
  error,
  helperText,
  required,
  disabled,
  totalCount,
}) => {
  const [search, setSearch] = useState("");

  const { handleMenuOpen, handleMenuClose } = useMenuScrollPagination({
    onSearch,
    onScrollEnd,
    options,
    search,
  });

  const handleClose = () => {
    // Clear search on close so next open shows full list
    setSearch("");
    onSearch("");
    handleMenuClose();
  };

  // ─── Toggle a single value in the multi-select array ─────────────────────
  const handleToggle = (opt: Option) => {
    const isSelected = value.some((v) => v?.value === opt?.value);

    const next = isSelected
      ? value.filter((v) => v?.value !== opt?.value)
      : [...value, opt];

    onChange(next.filter(Boolean));
    onRegister?.(next.filter(Boolean));
  };

  return (
    <FormControl fullWidth error={error} required={required} disabled={disabled}>
      <InputLabel>{label}</InputLabel>

      <Select
        multiple
        value={value.filter(v => v && v.value !== undefined).map((v) => v.value)}
        input={<OutlinedInput label={label} />}
        disabled={disabled}
        onOpen={handleMenuOpen}
        onClose={handleClose}
        onChange={(e) => {
          const selectedValues = e.target.value;
          const selectedArray = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
          
          // Map string values back to Option objects
          const newValue = selectedArray
            .map(val => {
              const existing = value.find(v => v && v.value === val);
              if (existing) return existing;
              const newOpt = options.find(o => o && o.value === val);
              return newOpt || { value: val, label: String(val) };
            })
            .filter(v => v && v.value !== undefined);
          
          onChange(newValue);
          onRegister?.(newValue);
        }}
        renderValue={() => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {value
              .filter(opt => opt && opt.label !== undefined)
              .map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                onDelete={(e) => {
                  e.stopPropagation();
                  handleToggle(opt);
                }}
                onMouseDown={(e) => e.stopPropagation()}
              />
            ))}
          </Box>
        )}
        MenuProps={{
          disableScrollLock: true,
          // Prevent menu from closing when clicking inside
          autoFocus: false,
          PaperProps: {
            sx: {
              maxHeight: 400,
              overflowY: "auto",
              overflowX: "hidden",
            },
          },
        }}
      >
        {/* Sticky search box */}
        <Box
          p={1}
          position="sticky"
          top={0}
          zIndex={1}
          bgcolor="background.paper"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TextField
            placeholder="Search..."
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            autoFocus
          />
          {totalCount !== undefined && (
            <Box display="flex" justifyContent="flex-end" mt={0.5} px={0.5}>
              <Typography variant="caption" color="text.secondary">
                Total options: {totalCount}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Options */}
        {options
          .filter(opt => opt && opt.value !== undefined && opt.label !== undefined)
          .map((opt) => (
          <MenuItem
            key={opt.value}
            value={opt.value}
            sx={{ py: 0.5 }}
          >
            <Checkbox
              checked={value.some((v) => v && v.value === opt.value)}
              size="small"
              sx={{ py: 0 }}
            />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}

        {/* Sticky loading indicator */}
        {loading && (
          <MenuItem
            disabled
            sx={{
              position: "sticky",
              bottom: 0,
              bgcolor: "background.paper",
              justifyContent: "center",
              py: 1,
            }}
          >
            <CircularProgress size={18} sx={{ mr: 1 }} />
            Loading more...
          </MenuItem>
        )}

        {!loading && options.length === 0 && (
          <MenuItem disabled>
            <em>No options available</em>
          </MenuItem>
        )}
      </Select>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};